import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookMarked, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/store/toastStore';
import {
  createKbChapter,
  kbQueryKeys,
  listKbChapters,
} from '@/services/knowledge-base.service';

export function ChaptersListPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');

  const chaptersQuery = useQuery({
    queryKey: kbQueryKeys.chapters(),
    queryFn: () => listKbChapters(),
  });

  const createMutation = useMutation({
    mutationFn: () => createKbChapter({ title: title.trim(), description, category, status: 'active' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.chapters() });
      toast.success('Chapter created');
      setShowCreate(false);
      setTitle('');
      setDescription('');
      setCategory('');
    },
    onError: () => toast.error('Failed to create chapter'),
  });

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    createMutation.mutate();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowCreate(true)}>
          New Chapter
        </Button>
      </div>

      {chaptersQuery.isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading chapters...
        </div>
      ) : !chaptersQuery.data?.length ? (
        <EmptyState
          icon={<BookMarked className="h-8 w-8" />}
          title="No chapters yet"
          description="Group related documents into ordered training modules."
          action={{ label: 'New Chapter', onClick: () => setShowCreate(true) }}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {chaptersQuery.data.map((c) => (
            <Link
              key={c.kbChapterId}
              to={`${c.kbChapterId}`}
              className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-200 hover:shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-900">{c.title}</h3>
                <Badge variant={c.status === 'active' ? 'success' : 'neutral'}>{c.status}</Badge>
              </div>
              {c.description && (
                <p className="mt-1 line-clamp-2 text-sm text-slate-600">{c.description}</p>
              )}
              <div className="mt-2 text-xs text-slate-500">
                {c.itemCount} {c.itemCount === 1 ? 'document' : 'documents'}
                {c.category ? ` • ${c.category}` : ''}
              </div>
            </Link>
          ))}
        </div>
      )}

      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Chapter"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={createMutation.isPending}>
              Create
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-3">
          <Input
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. New Hire Onboarding"
          />
          <Input
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Optional"
          />
          <Textarea autoResize
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </form>
      </Modal>
    </div>
  );
}
